import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    CircularProgress,
    Collapse,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EventIcon from "@mui/icons-material/Event";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "src/Redux/store";
import {
    clearTrip,
    deleteTrip,
    fetchMyTrips,
    selectTripPlanner,
} from "src/Redux/Slices/tripPlanner";
import {
    earliestQuoteStartDate,
    summariseQuoteItems,
} from "src/Utilities/tripPlannerHelpers";
import { ITripSummary } from "@interfaces";
import { LoadingStage } from "src/Interfaces/shared";
import { PATH_TRIP_PLANNER } from "@routes";
import { AppModuleLayout } from "@layouts";

const Wrapper = styled.div`
    max-width: 960px;
    margin: 0 auto;
    padding: 24px 16px;
`;

const TopBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    gap: 16px;
    flex-wrap: wrap;
`;

const TripList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const TripCard = styled.div`
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
`;

const TripHeader = styled.div`
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
`;

const TripName = styled.div`
    font-weight: 600;
    color: #1f2937;
    font-size: 16px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const MetaRow = styled.div`
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    margin-top: 4px;
    font-size: 13px;
    color: #6b7280;
`;

const MetaChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const Total = styled.span`
    color: #d32f2f;
    font-weight: 600;
`;

const Actions = styled.div`
    display: flex;
    gap: 4px;
    align-items: center;
`;

const ItemsList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0 16px 12px;
    border-top: 1px solid #f1f5f9;
`;

const ItemRow = styled.li`
    display: grid;
    grid-template-columns: 48px 1fr auto;
    gap: 12px;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px dashed #f1f5f9;

    &:last-child {
        border-bottom: none;
    }
`;

const Thumb = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 6px;
    background: #f3f4f6;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
`;

const ItemName = styled.div`
    font-weight: 500;
    color: #1f2937;
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ItemMeta = styled.div`
    font-size: 12px;
    color: #6b7280;
`;

const ItemPrice = styled.div`
    font-size: 14px;
    color: #1f2937;
    font-weight: 600;
    text-align: right;
`;

/* Always-visible preview strip: small thumbnail chips so the user can see
   at a glance which products & dates are in the trip without expanding.
   Mirrors the "thumbnail / name / date" trio from the expanded list at
   roughly half the size. */
const PreviewStrip = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 10px;
`;

const PreviewChip = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 10px 4px 4px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    max-width: 240px;
`;

const PreviewThumb = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #e5e7eb;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    flex-shrink: 0;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
`;

const PreviewLabel = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.15;
`;

const PreviewName = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: #1f2937;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 180px;
`;

const PreviewDate = styled.span`
    font-size: 11px;
    color: #6b7280;
`;

const MoreChip = styled.button`
    align-self: center;
    border: 1px dashed #cbd5e1;
    background: transparent;
    color: #475569;
    border-radius: 999px;
    font-size: 12px;
    padding: 4px 10px;
    cursor: pointer;

    &:hover {
        background: #f1f5f9;
    }
`;

const Empty = styled.div`
    text-align: center;
    color: #6b7280;
    padding: 48px 16px;
    border: 1px dashed #d1d5db;
    border-radius: 8px;
`;

const formatDate = (iso?: string) => {
    if (!iso) return "";
    const m = moment(iso, "YYYY-MM-DD");
    return m.isValid() ? m.format("D MMM YYYY") : "";
};

const formatShortDate = (iso?: string) => {
    if (!iso) return "";
    const m = moment(iso, "YYYY-MM-DD");
    return m.isValid() ? m.format("D MMM") : "";
};

/** How many product chips to show in the always-visible preview strip
 *  before collapsing the remainder into a single "+N more" affordance. */
const PREVIEW_LIMIT = 4;

/**
 * Compact always-visible strip of small thumbnail chips so the user can
 * see at a glance which products and on which dates are in each trip,
 * without having to expand the card. Pulls from the same snapshot-only
 * source as the expanded list (no TDMS calls). Clicking the "+N more"
 * chip expands the card to reveal the full per-item breakdown.
 */
const TripPreviewStrip = ({
    trip,
    onExpand,
    expanded,
}: {
    trip: ITripSummary;
    onExpand: () => void;
    expanded: boolean;
}) => {
    const items = useMemo(
        () => (trip.raw ? summariseQuoteItems(trip.raw) : []),
        [trip.raw],
    );
    if (items.length === 0) return null;
    const visible = items.slice(0, PREVIEW_LIMIT);
    const hidden = items.length - visible.length;
    return (
        <PreviewStrip aria-label={`Products in ${trip.name}`}>
            {visible.map((it, idx) => {
                const dateText = formatShortDate(it.startDate);
                return (
                    <PreviewChip
                        key={`${trip.id}-pv-${idx}`}
                        title={`${it.name}${dateText ? ` · ${dateText}` : ""}`}
                    >
                        <PreviewThumb aria-hidden="true">
                            {it.productImagePath ? (
                                <img src={it.productImagePath} alt="" loading="lazy" />
                            ) : (
                                <ImageNotSupportedIcon sx={{ fontSize: 16 }} />
                            )}
                        </PreviewThumb>
                        <PreviewLabel>
                            <PreviewName>{it.name}</PreviewName>
                            {dateText && <PreviewDate>{dateText}</PreviewDate>}
                        </PreviewLabel>
                    </PreviewChip>
                );
            })}
            {hidden > 0 && !expanded && (
                <MoreChip
                    type="button"
                    onClick={onExpand}
                    aria-label={`Show ${hidden} more item${hidden === 1 ? "" : "s"} in ${trip.name}`}
                >
                    +{hidden} more
                </MoreChip>
            )}
        </PreviewStrip>
    );
};

/**
 * Card body that renders the per-item digest pulled straight from the
 * saved quote snapshot. No TDMS call: every field below comes from
 * `summariseQuoteItems(trip.raw)` which reads the denormalised
 * `product.json` already on `/quotes/all`.
 */
const TripItemsPanel = ({ trip }: { trip: ITripSummary }) => {
    const items = useMemo(
        () => (trip.raw ? summariseQuoteItems(trip.raw) : []),
        [trip.raw],
    );
    if (items.length === 0) {
        return (
            <Box sx={{ px: 2, pb: 2, color: "#6b7280", fontSize: 13 }}>
                No items in this trip.
            </Box>
        );
    }
    return (
        <ItemsList aria-label={`Items in ${trip.name}`}>
            {items.map((it, idx) => (
                <ItemRow key={`${trip.id}-${idx}`}>
                    <Thumb aria-hidden="true">
                        {it.productImagePath ? (
                            <img src={it.productImagePath} alt="" loading="lazy" />
                        ) : (
                            <ImageNotSupportedIcon fontSize="small" />
                        )}
                    </Thumb>
                    <div style={{ minWidth: 0 }}>
                        <ItemName title={it.name}>{it.name}</ItemName>
                        <ItemMeta>
                            {/* Skip the date segment entirely when the
                                snapshot date is missing/malformed — never
                                surface "Invalid date" to the user. */}
                            {(() => {
                                const dateText = formatDate(it.startDate);
                                // `days` is the calendar footprint (N nights →
                                // N+1 days), so nights = days - 1.
                                const nights = it.days - 1;
                                const dayText =
                                    nights > 0
                                        ? `${nights} ${nights === 1 ? "night" : "nights"}`
                                        : "";
                                const qtyText = `${it.quantity}× ${
                                    it.fareName || "guest"
                                }`;
                                return [dateText, dayText, qtyText]
                                    .filter(Boolean)
                                    .join(" · ");
                            })()}
                        </ItemMeta>
                    </div>
                    <ItemPrice>${it.lineTotal.toFixed(0)}</ItemPrice>
                </ItemRow>
            ))}
        </ItemsList>
    );
};

/**
 * Lists every quote prefixed with `[Trip] ` (the marker the planner adds when
 * saving) so the user can re-open or delete planner-saved trips. Each row
 * is an elaborate card showing trip-level meta plus an expandable list of
 * the products in the trip — all rendered from the snapshot already
 * returned by `/quotes/all`, with no TDMS lookups.
 */
export const MyTrips = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { savedTrips, loadStage, myTripsDiagnostic } = useAppSelector(selectTripPlanner);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    useEffect(() => {
        dispatch(fetchMyTrips());
    }, [dispatch]);

    const handleNew = () => {
        dispatch(clearTrip());
        navigate(PATH_TRIP_PLANNER);
    };

    const handleOpen = (id: string | number) => {
        navigate(`${PATH_TRIP_PLANNER}/${id}`);
    };

    const handleDelete = (id: string | number) => {
        // Confirm via a native dialog to avoid pulling in another modal lib
        // for a destructive action that would otherwise be one-click on
        // mobile.
        // eslint-disable-next-line no-alert
        if (!window.confirm("Delete this trip? This can't be undone.")) return;
        dispatch(deleteTrip({ quoteId: id }));
    };

    const toggleExpand = (id: string | number) => {
        const key = String(id);
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const isLoading = loadStage === LoadingStage.IS_LOADING && savedTrips.length === 0;

    // Wrap in the shared shell so My Trips has the global header (account
    // menu, cart, etc.) consistent with the rest of the app.
    return (
        <AppModuleLayout>
            <Wrapper>
            <TopBar>
                <Typography variant="h5">My Trips</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleNew}
                >
                    New Trip
                </Button>
            </TopBar>

            {isLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress size={32} />
                </Box>
            )}

            {!isLoading && savedTrips.length === 0 && (
                <Empty>
                    <Typography sx={{ mb: 1 }}>You haven't saved any trips yet.</Typography>
                    <Button variant="text" onClick={handleNew}>
                        Plan your first trip
                    </Button>
                    {/* Diagnostic strip — only shown when the list is empty.
                        Reveals whether /quotes/my returned 0 quotes (auth /
                        account scoping issue), returned quotes whose titles
                        don't look like trips (backend renamed them) or
                        errored. Removing once the empty-list pathway is
                        understood for this account. */}
                    {myTripsDiagnostic && (
                        <Box
                            sx={{
                                mt: 3,
                                pt: 2,
                                borderTop: "1px dashed #d1d5db",
                                fontSize: 12,
                                color: "#6b7280",
                                textAlign: "left",
                            }}
                        >
                            <strong>Debug:</strong> received{" "}
                            {myTripsDiagnostic.totalQuotes} quote
                            {myTripsDiagnostic.totalQuotes === 1 ? "" : "s"} from the server;{" "}
                            {myTripsDiagnostic.matchedAsTrip} matched as a trip.
                            {myTripsDiagnostic.error && (
                                <div style={{ marginTop: 4, color: "#b91c1c" }}>
                                    Error: {myTripsDiagnostic.error}
                                </div>
                            )}
                            {myTripsDiagnostic.sampleTitles.length > 0 && (
                                <div style={{ marginTop: 4 }}>
                                    First titles returned:
                                    <ul style={{ margin: "4px 0 0 16px" }}>
                                        {myTripsDiagnostic.sampleTitles.map((t, i) => (
                                            <li key={i}>
                                                {t === "" ? "(empty title)" : JSON.stringify(t)}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </Box>
                    )}
                </Empty>
            )}

            {!isLoading && savedTrips.length > 0 && (
                <TripList>
                    {savedTrips.map((t) => {
                        const key = String(t.id);
                        const isOpen = !!expanded[key];
                        const startDate = t.raw
                            ? earliestQuoteStartDate(t.raw)
                            : undefined;
                        return (
                            <TripCard key={key}>
                                <TripHeader>
                                    <div style={{ minWidth: 0 }}>
                                        <TripName title={t.name}>{t.name}</TripName>
                                        <MetaRow>
                                            {startDate && (
                                                <MetaChip>
                                                    <EventIcon
                                                        sx={{ fontSize: 14 }}
                                                        aria-hidden="true"
                                                    />
                                                    {formatDate(startDate)}
                                                </MetaChip>
                                            )}
                                            <MetaChip>
                                                {t.itemCount} item
                                                {t.itemCount === 1 ? "" : "s"}
                                            </MetaChip>
                                            {Number.isFinite(t.totalPrice) &&
                                                t.totalPrice > 0 && (
                                                    <MetaChip>
                                                        <Total>
                                                            ${t.totalPrice.toFixed(0)}
                                                        </Total>
                                                    </MetaChip>
                                                )}
                                        </MetaRow>
                                        <TripPreviewStrip
                                            trip={t}
                                            onExpand={() => toggleExpand(t.id)}
                                            expanded={isOpen}
                                        />
                                    </div>
                                    <Actions>
                                        <Tooltip
                                            title={
                                                isOpen ? "Hide items" : "Show items"
                                            }
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() => toggleExpand(t.id)}
                                                aria-label={`${
                                                    isOpen ? "Hide" : "Show"
                                                } items in ${t.name}`}
                                                aria-expanded={isOpen}
                                                disabled={t.itemCount === 0}
                                            >
                                                {isOpen ? (
                                                    <ExpandLessIcon fontSize="small" />
                                                ) : (
                                                    <ExpandMoreIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Open trip">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpen(t.id)}
                                                aria-label={`Open trip ${t.name}`}
                                            >
                                                <OpenInNewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete trip">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDelete(t.id)}
                                                aria-label={`Delete trip ${t.name}`}
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Actions>
                                </TripHeader>
                                <Collapse in={isOpen} unmountOnExit>
                                    <TripItemsPanel trip={t} />
                                </Collapse>
                            </TripCard>
                        );
                    })}
                </TripList>
            )}
            </Wrapper>
        </AppModuleLayout>
    );
};

export default MyTrips;
